import { Download, Eye, Loader2, PenLine, RefreshCw, Send } from 'lucide-react';
import { useState } from 'react';
import type { AccountContact } from '@/api/commercial/accounts';
import { ProposalStatus } from '@/api/commercial/proposals';
import type { ProposalContractViewModel } from '@/components/commercial/proposals/contracts/proposal-contract-types';
import { Button } from '@/components/ui/button';
import type { ValidationMessage } from '@/hooks/commercial/use-resume-validation';
import type { IProposalCartItem } from '@/types/commercial/cart';
import { ResumeAcceptanceHistoryDialog } from './resume-acceptance-history-dialog';
import { ResumeCancelDialog } from './resume-cancel-dialog';
import { ResumeReopenDialog } from './resume-reopen-dialog';
import { ResumeSubmitDialog } from './resume-submit-dialog';

interface ResumeHeaderActionsProps {
  proposalId?: string;
  status?: string;
  sentAt?: string | null;
  onSave: () => void;
  isSaving?: boolean;
  onUpdate?: () => void;
  onReopen?: () => void;
  contracts?: ProposalContractViewModel[];
  onViewContracts?: () => void;
  onViewContract?: (contract: ProposalContractViewModel) => void;
  onDownloadContract?: (contract: ProposalContractViewModel) => void;
  showContractsAction?: boolean;
  contractsUnavailableReason?: string;
}

export function ResumeHeaderActions({
  proposalId,
  status,
  sentAt,
  onSave,
  isSaving = false,
  onUpdate,
  onReopen,
  contracts,
  onViewContracts,
  onViewContract,
  onDownloadContract,
  showContractsAction = false,
  contractsUnavailableReason,
}: ResumeHeaderActionsProps) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const isOpen = status === ProposalStatus.OPEN;
  const isPendingCredit = status === ProposalStatus.PENDING_CREDIT;
  const isPendingAccept = status === ProposalStatus.PENDING_ACCEPT;
  const isRefused = status === ProposalStatus.REFUSED;
  const isExpired = status === ProposalStatus.EXPIRED;
  const canCancel = isOpen || isPendingCredit || isPendingAccept;
  const canReopen = (isPendingCredit || isPendingAccept || isRefused || isExpired) && onReopen;
  const canShowAcceptanceHistory = sentAt != null;
  const hasMultipleContracts = Boolean(contracts && contracts.length > 1 && onViewContracts);
  const hasSingleContract = contracts?.length === 1;

  return (
    <>
      <div className="flex max-w-full flex-col items-end gap-2 self-start">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" onClick={() => setCancelDialogOpen(true)} disabled={!canCancel}>
            Cancelar Proposta
          </Button>
          {canShowAcceptanceHistory && (
            <Button type="button" variant="outline" onClick={() => setHistoryDialogOpen(true)}>
              Histórico de aceite
            </Button>
          )}
          {showContractsAction && hasSingleContract && contracts?.[0]?.canView && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onViewContract?.(contracts[0])}
              >
                <Eye className="mr-2 h-4 w-4" />
                Visualizar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onDownloadContract?.(contracts[0])}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </>
          )}
          {showContractsAction && !hasSingleContract && (
            <Button
              variant="outline"
              onClick={hasMultipleContracts ? onViewContracts : undefined}
              disabled={!hasMultipleContracts}
            >
              Ver Contrato
            </Button>
          )}
          {isOpen && (
            <Button
              className="bg-black text-white hover:bg-gray-800"
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          )}
          {canReopen && (
            <Button
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => setReopenDialogOpen(true)}
            >
              <PenLine className="mr-1 h-4 w-4" />
              Editar Proposta
            </Button>
          )}
        </div>

        {showContractsAction && !hasSingleContract && !hasMultipleContracts && (
          <p className="max-w-sm text-right text-xs text-muted-foreground break-words">
            {contractsUnavailableReason}
          </p>
        )}
        {showContractsAction && hasSingleContract && !contracts?.[0]?.canView && (
          <p className="max-w-sm text-right text-xs text-muted-foreground break-words">
            {contracts[0].unavailableReason}
          </p>
        )}
      </div>

      <ResumeCancelDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        proposalId={proposalId}
        onSuccess={onUpdate}
      />

      <ResumeReopenDialog
        open={reopenDialogOpen}
        onClose={() => setReopenDialogOpen(false)}
        proposalId={proposalId}
        onSuccess={onReopen}
      />

      <ResumeAcceptanceHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        proposalId={proposalId}
      />
    </>
  );
}

interface ResumeFooterActionsProps {
  proposalId?: string;
  clientName?: string;
  status?: string;
  canSubmit: boolean;
  blockers: ValidationMessage[];
  cartItems: IProposalCartItem[];
  contacts: AccountContact[];
  onUpdate?: () => void;
}

export function ResumeFooterActions({
  proposalId,
  clientName,
  status,
  canSubmit,
  blockers,
  cartItems,
  contacts,
  onUpdate,
}: ResumeFooterActionsProps) {
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);

  const isOpen = status === ProposalStatus.OPEN;
  const isPendingAccept = status === ProposalStatus.PENDING_ACCEPT;
  const creditAnalysisBlocker = blockers.find(
    (blocker) => blocker.code === 'CREDIT_ANALYSIS_PENDING',
  );

  if (!isOpen && !isPendingAccept) {
    return null;
  }

  return (
    <>
      <div className="mt-4 flex flex-col items-end gap-2 border-t border-gray-200 pt-4">
        {isOpen && creditAnalysisBlocker && (
          <p className="text-sm text-amber-700">{creditAnalysisBlocker.message}</p>
        )}
        {isOpen && (
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => setSubmitDialogOpen(true)}
            disabled={!canSubmit}
            title={!canSubmit ? blockers[0]?.message : undefined}
          >
            <Send className="mr-2 h-4 w-4" />
            Enviar proposta
          </Button>
        )}
        {isPendingAccept && (
          <Button
            className="bg-black text-white hover:bg-gray-800"
            onClick={() => setSubmitDialogOpen(true)}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reenviar proposta
          </Button>
        )}
      </div>

      <ResumeSubmitDialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        proposalId={proposalId}
        clientName={clientName}
        cartItems={cartItems}
        contacts={contacts}
        mode={isPendingAccept ? 'resend' : 'submit'}
        onSuccess={onUpdate}
      />
    </>
  );
}
